import { bind, define, getHost, html, onCleanup, onMounted, prop, useEmit, useField, useSlots } from '@vielzeug/ore';
import { computed, effect, signal, watch } from '@vielzeug/ripple';

import type { OverlayOpenChangeDetail, OverlayOpenReason } from '../../core';
import { createChoiceField, createListboxDropdown, lifecycleSignal } from '../../core';
import type { SelectableFieldProps } from '../../shared';
import type { VisualVariant } from '../../types';
import '../../feedback/chip/chip';
import '../../content/icon/icon';
import '../input/input';
import { disablableBundle, loadableBundle, roundableBundle, sizableBundle, themableBundle } from '../../shared';
import { colorThemeMixin, reducedMotionMixin, roundedVariantMixin } from '../../styles';
import { defineFieldValue, dispatchNativeFieldEvent, setFieldValue } from '../shared/native-field-event';
import componentStyles from './select.css?inline';

// ── Types ─────────────────────────────────────────────────────────────

type OptionItem = {
  disabled: boolean;
  group?: string;
  label: string;
  value: string;
};

export type OreSelectOptionInput = {
  disabled?: boolean;
  group?: string;
  label?: string;
  value: string;
};

type FlatRow =
  | {
      idx: number;
      opt: OptionItem;
      type: 'option';
    }
  | {
      label: string;
      type: 'group';
    };

// ── Styles ─────────────────────────────────────────────────────────────

// ── Component Props ─────────────────────────────────────────────────────────────

/** Select component properties */

export type OreSelectEvents = {
  change: Event;
  input: Event;
  'open-change': OverlayOpenChangeDetail;
};

export type OreSelectProps = SelectableFieldProps<Exclude<VisualVariant, 'frost'>> & {
  /** Show loading state in dropdown */
  loading?: boolean;
  /** Allow selecting multiple options */
  multiple?: boolean;
  /** JS options array (alternative to slotted <option> elements) */
  options?: OreSelectOptionInput[];
  /** Mark the field as required */
  required?: boolean;
};

/**
 * A fully custom form-associated select dropdown with keyboard navigation and ARIA support.
 * Reads `<option>` and `<optgroup>` children from the default slot.
 *
 * @element ore-select
 *
 * @attr {string} label - Label text
 * @attr {string} label-placement - 'inset' | 'outside'
 * @attr {string} value - Current selected value(s) (comma-separated for multiple)
 * @attr {string} placeholder - Placeholder when no option selected
 * @attr {string} name - Form field name
 * @attr {boolean} multiple - Enable multi-select
 * @attr {boolean} disabled - Disable the select
 * @attr {boolean} required - Required field
 * @attr {string} helper - Helper text below the select
 * @attr {string} error - Error message
 * @attr {boolean} success - Show an inline success check icon (suppressed while `error` is set)
 * @attr {string} color - Theme color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
 * @attr {string} variant - Visual variant: 'solid' | 'flat' | 'bordered' | 'outline' | 'ghost' | 'text'
 * @attr {string} size - Component size: 'sm' | 'md' | 'lg'
 * @attr {string} rounded - Border radius: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'
 * @attr {boolean} fullwidth - Expand to full width
 *
 * @fires input - Fired when selection changes.
 * @fires change - Fired when selection changes.
 * @fires open-change - Fired when the dropdown state changes. detail: { open, reason }
 *
 * @slot - `<option>` and `<optgroup>` elements
 *
 * @cssprop --select-bg - Background
 * @cssprop --select-border-color - Border color
 * @cssprop --select-radius - Border radius
 * @cssprop --select-padding - Padding
 * @cssprop --select-font-size - Font size
 * @cssprop --select-placeholder-color - Placeholder text color
 *
 * @part trigger - The `ore-input` trigger element that opens the dropdown.
 * @part dropdown - The dropdown listbox panel container.
 *
 * @example
 * ```html
 * <ore-select label="Role" value="admin">
 *   <option value="admin">Administrator</option>
 *   <option value="editor">Editor</option>
 *   <option value="viewer">Viewer</option>
 * </ore-select>
 *
 * <ore-select label="Tags" multiple color="primary">
 *   <optgroup label="Frontend">
 *     <option value="react">React</option>
 *     <option value="vue">Vue</option>
 *   </optgroup>
 *   <optgroup label="Backend">
 *     <option value="node">Node.js</option>
 *   </optgroup>
 * </ore-select>
 * ```
 */
export const SELECT_TAG = 'ore-select' as const;
define<OreSelectProps>(SELECT_TAG, {
  formAssociated: true,
  props: {
    ...themableBundle,
    ...sizableBundle,
    ...disablableBundle,
    ...loadableBundle,
    ...roundableBundle,
    error: prop.string(),
    fullwidth: prop.bool(false),
    helper: prop.string(),
    label: prop.string(),
    'label-placement': prop.oneOf(['inset', 'outside'] as const, 'inset'),
    multiple: prop.bool(false),
    name: prop.string(),
    options: prop.json(undefined as OreSelectOptionInput[] | undefined),
    placeholder: prop.string(),
    required: prop.bool(false),
    success: prop.bool(false),
    value: prop.string(),
    variant: prop.string<Exclude<VisualVariant, 'frost'>>(),
  },
  setup(props) {
    const el = getHost();
    const emit = useEmit<OreSelectEvents>();
    const slots = useSlots();

    const shadowRoot = el.shadowRoot;
    // ────────────────────────────────────────────────────────────────
    // State & Context
    // ────────────────────────────────────────────────────────────────

    const slottedOptions = signal<OptionItem[]>([]);
    const isLoading = computed(() => Boolean(props.loading.value));

    // Merged options: explicit prop value overrides slotted options
    function normalizeOption(option: OreSelectOptionInput): OptionItem {
      return {
        disabled: Boolean(option.disabled),
        group: option.group,
        label: option.label ?? option.value,
        value: option.value,
      };
    }

    const options = computed(() => {
      const explicitOptions = props.options.value;

      return Array.isArray(explicitOptions) ? explicitOptions.map(normalizeOption) : slottedOptions.value;
    });
    let triggerEl: HTMLElement | null = null;
    let dropdownEl: HTMLElement | null = null;

    const abortSignal = lifecycleSignal(onCleanup);
    const choice = createChoiceField({
      disabled: props.disabled,
      error: props.error,
      helper: props.helper,
      label: props.label,
      labelPlacement: props['label-placement'],
      multiple: props.multiple,
      prefix: 'select',
      required: props.required,
      signal: abortSignal,
      value: props.value,
    });

    choice.attachFormField(
      useField<string>({
        disabled: choice.disabled,
        onReset: choice.reset,
        toFormValue: (v) => v,
        validationMessage: choice.validationMessage,
        validity: choice.validity,
        value: choice.formValue,
      }),
    );

    const optionList = createListboxDropdown<OptionItem>({
      getBoundary: () => el,
      getFocusedOptionElement: () => dropdownEl?.querySelector<HTMLElement>('[data-focused]') ?? null,
      getItems: () => options.value,
      getPanel: () => dropdownEl,
      getReference: () => triggerEl,
      getTrigger: () => triggerEl,
      isDisabled: () => choice.disabled.value,
      isItemDisabled: (option) => option.disabled,
      onClose: (reason) => {
        emit('open-change', { open: false, reason });
        choice.triggerValidation('blur');
      },
      onOpen: (reason) => emit('open-change', { open: true, reason }),
      signal: abortSignal,
    });

    const { triggerValidation } = choice;
    const selectedValues = choice.selectedValues;
    const isDisabled = choice.disabled;

    defineFieldValue(
      el,
      () => choice.formValue.value,
      (value) => {
        choice.setValues(value ? value.split(',') : []);
      },
    );

    const { fieldId: selectId } = choice;
    const listboxId = `listbox-${selectId}`;
    const { focusedIndex, isOpen } = optionList;

    // Sync host attributes for CSS hooks
    bind({
      attr: {
        'has-error': () => (props.error.value ? true : undefined),
        open: () => (isOpen.value ? true : undefined),
        size: props.size,
        variant: props.variant,
      },
    });

    // ────────────────────────────────────────────────────────────────
    // Option Reading from Slot
    // ────────────────────────────────────────────────────────────────

    function readOptions() {
      const slot = shadowRoot?.querySelector<HTMLSlotElement>('slot');

      if (!slot) return;

      const assigned = slot.assignedElements({ flatten: true });
      const items: OptionItem[] = [];

      for (const el of assigned) {
        if (el.tagName === 'OPTION') {
          const opt = el as HTMLOptionElement;

          items.push({ disabled: opt.disabled, label: opt.text || opt.value, value: opt.value });
        } else if (el.tagName === 'OPTGROUP') {
          const group = el as HTMLOptGroupElement;
          const groupLabel = group.label;

          for (const child of Array.from(group.querySelectorAll('option'))) {
            const opt = child as HTMLOptionElement;

            items.push({ disabled: opt.disabled, group: groupLabel, label: opt.text || opt.value, value: opt.value });
          }
        }
      }

      slottedOptions.value = items;
    }

    const displayLabel = computed(() => {
      if (selectedValues.value.length === 0) return '';

      if (props.multiple.value && selectedValues.value.length > 1) {
        return `${selectedValues.value.length} selected`;
      }

      const first = selectedValues.value[0];

      return options.value.find((o) => o.value === first)?.label ?? first;
    });
    const selectedChipItems = computed(() => {
      if (!props.multiple.value) return [];

      return selectedValues.value.map((value) => ({
        label: options.value.find((o) => o.value === value)?.label ?? value,
        value,
      }));
    });
    const showChips = computed(() => props.multiple.value && selectedValues.value.length > 0);
    const triggerText = computed(() => displayLabel.value || props.placeholder.value || '');

    function buildFlatList(opts: OptionItem[]): FlatRow[] {
      const flat: FlatRow[] = [];
      const groups = new Map<string | undefined, OptionItem[]>();

      for (const opt of opts) {
        const key = opt.group;

        let group = groups.get(key);

        if (!group) {
          group = [];
          groups.set(key, group);
        }

        group.push(opt);
      }

      let globalIdx = 0;

      for (const [groupLabel, groupOpts] of groups) {
        if (groupLabel !== undefined) flat.push({ label: groupLabel, type: 'group' });

        for (const opt of groupOpts) flat.push({ idx: globalIdx++, opt, type: 'option' });
      }

      return flat;
    }

    const flatRows = computed(() => buildFlatList(options.value));

    function emitChange(): void {
      setFieldValue(el, choice.formValue.value);
      dispatchNativeFieldEvent(el, 'input');
      dispatchNativeFieldEvent(el, 'change');
    }

    function removeChip(event: Event): void {
      event.stopPropagation();

      const value = (event as CustomEvent<{ value?: string }>).detail?.value;

      if (value === undefined) return;

      choice.removeValue(value);
      emitChange();
      triggerValidation('change');
    }

    function openPopup(reason: OverlayOpenReason = 'programmatic') {
      optionList.open(reason);

      requestAnimationFrame(() => {
        const selectedIndex =
          selectedValues.value.length > 0
            ? options.value.findIndex((option) => option.value === selectedValues.value[0])
            : -1;

        if (selectedIndex >= 0) {
          optionList.set(selectedIndex);

          return;
        }

        optionList.navigate('first');
      });
    }

    // Selection

    function selectOption(opt: OptionItem, _e?: Event) {
      if (opt.disabled) return;

      if (props.multiple.value) {
        choice.toggleValue(opt.value);
      } else {
        choice.selectValue(opt.value);
        optionList.close();
      }

      emitChange();
      triggerValidation('change');
    }

    // Keyboard navigation

    function handleTriggerKeydown(e: KeyboardEvent) {
      if (isDisabled.value) return;

      // Let optionList handle arrow key navigation
      if (optionList.handleKeydown(e)) return;

      switch (e.key) {
        case ' ':
        case 'Enter':
          e.preventDefault();

          if (isOpen.value) {
            const idx = focusedIndex.value;
            const opts = options.value;

            if (idx >= 0 && idx < opts.length) selectOption(opts[idx], e);
          } else {
            openPopup('keyboard');
          }

          break;
        case 'Tab':
          optionList.close();
          break;
      }
    }

    // ore-input prop helpers
    const inputValue = () => triggerText.value;
    const inputLabel = () => props.label.value ?? '';
    const inputPlaceholder = () => props.placeholder.value ?? '';
    const inputLabelPlacement = () => props['label-placement'].value ?? 'inset';
    const inputColor = () => props.color?.value ?? undefined;
    const inputSize = () => props.size?.value ?? undefined;
    const inputVariant = () => props.variant?.value ?? undefined;
    const inputRounded = () => props.rounded?.value ?? undefined;
    const inputHelper = () => props.helper.value ?? '';
    const inputError = () => props.error.value ?? '';
    const inputDisabled = () => (isDisabled.value ? true : undefined);
    const inputRequired = () => (props.required.value ? true : undefined);
    const inputFullwidth = () => (props.fullwidth.value ? true : undefined);
    const tabIndexAttr = () => (isDisabled.value ? '-1' : '0');

    watch(slots.elements(), () => readOptions(), { immediate: true });

    const stopPopoverSync = effect(() => {
      if (isOpen.value) {
        if (dropdownEl && 'showPopover' in dropdownEl && !dropdownEl.matches(':popover-open')) dropdownEl.showPopover();
      } else {
        if (dropdownEl && 'hidePopover' in dropdownEl && dropdownEl.matches(':popover-open')) dropdownEl.hidePopover();
      }
    });

    onCleanup(() => stopPopoverSync.dispose());

    onMounted(() => {
      let onTriggerClick: ((event: MouseEvent) => void) | null = null;
      let onTriggerKeydown: ((event: KeyboardEvent) => void) | null = null;

      if (triggerEl) {
        onTriggerClick = (event: MouseEvent) => {
          event.stopPropagation();

          if (isDisabled.value) return;

          if (isOpen.value) optionList.close('trigger');
          else openPopup('click');
        };
        onTriggerKeydown = (event: KeyboardEvent) => {
          handleTriggerKeydown(event);
        };

        triggerEl.addEventListener('click', onTriggerClick);
        triggerEl.addEventListener('keydown', onTriggerKeydown);
      }

      return () => {
        if (triggerEl && onTriggerClick) triggerEl.removeEventListener('click', onTriggerClick);

        if (triggerEl && onTriggerKeydown) triggerEl.removeEventListener('keydown', onTriggerKeydown);
      };
    });

    return html`
      <slot style="display:none"></slot>
      <ore-input
        class="trigger"
        part="trigger"
        readonly
        tabindex="${tabIndexAttr}"
        role="combobox"
        aria-haspopup="listbox"
        aria-controls="${listboxId}"
        aria-disabled="${() => (isDisabled.value ? 'true' : null)}"
        aria-expanded="${() => String(isOpen.value)}"
        aria-invalid="${() => (props.error.value ? 'true' : null)}"
        value="${inputValue}"
        label="${inputLabel}"
        placeholder="${inputPlaceholder}"
        label-placement="${inputLabelPlacement}"
        color="${inputColor}"
        size="${inputSize}"
        variant="${inputVariant}"
        rounded="${inputRounded}"
        helper="${inputHelper}"
        error="${inputError}"
        ?disabled="${inputDisabled}"
        ?required="${inputRequired}"
        ?fullwidth="${inputFullwidth}"
        ?success="${() => props.success.value}"
        ref="${(el: HTMLElement) => {
          triggerEl = el;
        }}">
        <div slot="prefix" class="chips-row" ?hidden="${() => !showChips.value}">
          ${() =>
            selectedChipItems.value.map(
              (item) => html`
                <ore-chip
                  value="${item.value}"
                  label="${item.label}"
                  mode="removable"
                  variant="flat"
                  size="sm"
                  color="${props.color}"
                  @remove="${removeChip}">
                  ${item.label}
                </ore-chip>
              `,
            )}
        </div>
        <span slot="suffix" class="trigger-suffix" aria-hidden="true">
          <span class="loader"></span>
          <span class="trigger-icon">
            <ore-icon name="chevron-down" size="14" stroke-width="2" aria-hidden="true"></ore-icon>
          </span>
        </span>
      </ore-input>
      <div
        class="dropdown"
        part="dropdown"
        popover="manual"
        ?data-open="${isOpen}"
        role="listbox"
        id="${listboxId}"
        aria-label="Options"
        ref="${(el: HTMLElement) => {
          dropdownEl = el;
        }}">
        <div class="dropdown-loading" ?hidden="${() => !isLoading.value}">Loading…</div>
        <div class="dropdown-empty" ?hidden="${() => isLoading.value || options.value.length > 0}">No options</div>
        <div class="options-list" ?hidden="${() => isLoading.value || options.value.length === 0}">
          ${() =>
            flatRows.value.map((row) =>
              row.type === 'group'
                ? html`
                    <div class="optgroup-label" role="presentation">${row.label}</div>
                  `
                : html`
                    <div
                      class="option"
                      role="option"
                      id="${`${selectId}-opt-${row.idx}`}"
                      data-option-index="${String(row.idx)}"
                      data-option-value="${row.opt.value}"
                      aria-selected="${() => String(selectedValues.value.includes(row.opt.value))}"
                      aria-disabled="${() => String(row.opt.disabled)}"
                      ?data-focused="${() => focusedIndex.value === row.idx}"
                      ?data-selected="${() => selectedValues.value.includes(row.opt.value)}"
                      ?data-disabled="${() => row.opt.disabled}"
                      @click="${(e: MouseEvent) => {
                        e.stopPropagation();
                        selectOption(row.opt, e);
                      }}"
                      @pointerenter="${() => {
                        optionList.set(row.idx);
                      }}">
                      <span>${row.opt.label}</span>
                      <span class="option-check" aria-hidden="true">
                        <ore-icon name="check" size="14" stroke-width="2.5" aria-hidden="true"></ore-icon>
                      </span>
                    </div>
                  `,
            )}
        </div>
      </div>
    `;
  },
  shadow: { delegatesFocus: true },
  styles: [colorThemeMixin, reducedMotionMixin, roundedVariantMixin, componentStyles],
});
